import React from "react";
import { Box, Flex, HStack, Icon, Text, Tooltip } from "@chakra-ui/react";
import { ArtifactProto } from "build-ts/chat_session";
import { formatToolRunDuration } from "../../../utils/helperFuncUtils";
import {
  toolRunStreamIcon,
  resolveArtifactsForSuccessfulToolRun,
  resolveErrorForFailedToolRun,
  type ToolExecutionRun,
} from "../../../utils/executionStepRun";
import { AppIcons } from "constants/icons";
import { toolRunLabelParts } from "utils/executionStepStatusLabels";
import { RESULT_PILL_KIND } from "modules/artifacts/dataframe/utils/dataFrameCodeUtils";
import { ArtifactOpenOpts } from "../../interfaces";
import { ArtifactLink } from "../../common/ArtifactLink";

export interface ToolRunRowProps {
  run: ToolExecutionRun;
  isFirstRun: boolean;
  isLastRun: boolean;
  prevRunDone: boolean;
  onArtifactClick?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
}

// One timeline row for a completed/in-progress tool execution.
const ToolRunRow: React.FC<ToolRunRowProps> = ({ run, isFirstRun, isLastRun, prevRunDone, onArtifactClick }) => {
  const topConnectorBg = isFirstRun ? "transparent" : prevRunDone ? "gray.300" : "gray.200";
  const labelParts = toolRunLabelParts(run);
  const done = run.completed || run.failed;
  const StreamIcon = toolRunStreamIcon(run);
  const durationStr = done ? formatToolRunDuration(run.steps) : null;
  const openArtifacts = resolveArtifactsForSuccessfulToolRun(run);
  const failureError = resolveErrorForFailedToolRun(run);
  const hasItemLinks = done && !!labelParts.artifactItems && labelParts.artifactItems.length > 1;
  const isRunClickable = !!onArtifactClick && openArtifacts.length > 0 && !hasItemLinks;

  const durationSuffix = durationStr ? (
    <Text
      as="span"
      fontSize="sm"
      color="gray.500"
      fontWeight="500"
      mb={0}
      ml={1}
      lineHeight="1.25"
      whiteSpace="nowrap"
      flexShrink={0}
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      {durationStr}
    </Text>
  ) : null;

  // Shared activation logic for onClick and onKeyDown.
  const renderLabel = () => {
    // Branch 1: multiple individually-clickable outputs (e.g. several charts/dataframes).
    if (hasItemLinks) {
      return (
        <HStack spacing={1} align="center" minW={0} flexWrap="wrap" flexShrink={1}>
          <Text fontSize="sm" color="gray.700" fontWeight="500" lineHeight="1.25" mb={0} as="span" flexShrink={0}>
            {labelParts.prefix}
          </Text>
          {labelParts.artifactItems!.map((item, idx) => (
            <React.Fragment key={item.label}>
              {idx > 0 && (
                <Text as="span" fontSize="sm" color="gray.500" fontWeight="500" mb={0} lineHeight="1.25">
                  ,
                </Text>
              )}
              <ArtifactLink
                variant="chip"
                isActive={!!onArtifactClick && !!openArtifacts[0]}
                onClick={() => {
                  if (onArtifactClick && openArtifacts[0]) {
                    onArtifactClick(openArtifacts[0], {
                      activeTab: item.activeTab,
                      subResourceName: item.subResourceName,
                    });
                  }
                }}
              >
                <Icon
                  as={
                    item.iconType === RESULT_PILL_KIND.chart
                      ? AppIcons.chart
                      : item.iconType === RESULT_PILL_KIND.report
                        ? AppIcons.document
                        : AppIcons.dataframe
                  }
                  boxSize={3.5}
                  color="gray.500"
                  flexShrink={0}
                  aria-hidden
                />
                <Text fontSize="sm" color="gray.900" fontWeight="600" lineHeight="1.25" mb={0} as="span" minW={0}>
                  {item.label}
                </Text>
              </ArtifactLink>
            </React.Fragment>
          ))}
          {durationSuffix}
        </HStack>
      );
    }

    // Branch 2: single highlighted artifact — prefix + tool icon + artifact name.
    if (done && labelParts.artifactHighlight) {
      return (
        <HStack spacing={1} align="flex-start" minW={0} flexWrap="wrap" flexShrink={1}>
          <Text
            fontSize="sm"
            color="gray.700"
            fontWeight="500"
            lineHeight="1.25"
            mb={0}
            as="span"
            flexShrink={0}
            whiteSpace="nowrap"
          >
            {labelParts.prefix}
          </Text>
          <Icon as={StreamIcon} boxSize={4} color="gray.500" flexShrink={0} mt="1px" aria-hidden />
          <Text
            fontSize="sm"
            color="gray.900"
            fontWeight="600"
            lineHeight="1.25"
            mb={0}
            as="span"
            minW={0}
            textAlign="left"
            wordBreak="break-word"
          >
            {labelParts.artifactHighlight}
            {durationStr && (
              <Text
                as="span"
                fontSize="sm"
                color="gray.500"
                fontWeight="500"
                ml={1}
                lineHeight="1.25"
                whiteSpace="nowrap"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {durationStr}
              </Text>
            )}
          </Text>
        </HStack>
      );
    }

    // Branch 3: plain status text only (in-progress, failed with no artifact, or no highlight).
    return (
      <Text
        fontSize="sm"
        color="gray.700"
        fontWeight="500"
        lineHeight="1.25"
        mb={0}
        textAlign="left"
        minW={0}
        flexShrink={1}
      >
        {labelParts.prefix}
        {durationStr ? (
          <>
            {" "}
            <Text as="span" color="gray.400" fontWeight="500" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {durationStr}
            </Text>
          </>
        ) : null}
      </Text>
    );
  };

  const openWithOpts = () => {
    if (!onArtifactClick) return;
    const singleItem = labelParts.artifactItems?.length === 1 ? labelParts.artifactItems[0] : undefined;
    const singleOpts = singleItem
      ? { activeTab: singleItem.activeTab, subResourceName: singleItem.subResourceName }
      : undefined;
    openArtifacts.forEach(a => onArtifactClick(a, singleOpts));
  };

  const node = run.failed ? (
    <Icon as={AppIcons.errorOutline} boxSize={4} color="red.500" />
  ) : done ? (
    <Icon as={AppIcons.checkCircle} boxSize={4} color="green.500" />
  ) : (
    <>
      <Box
        position="absolute"
        inset="-1px"
        borderRadius="full"
        pointerEvents="none"
        sx={{
          "@keyframes timelineGlow": {
            "0%, 100%": {
              opacity: 0.4,
              boxShadow: "0 0 0 0 rgba(251, 207, 232, 0.35)",
            },
            "50%": {
              opacity: 0.75,
              boxShadow: "0 0 0 3px rgba(251, 207, 232, 0.2)",
            },
          },
          animation: "timelineGlow 2.4s ease-in-out infinite",
          bg: "pink.50",
        }}
      />
      <Icon as={StreamIcon} boxSize={4} color="gray.500" zIndex={1} />
    </>
  );

  return (
    <Tooltip
      label={failureError}
      isDisabled={!failureError}
      hasArrow
      placement="top"
      maxW="320px"
      whiteSpace="pre-wrap"
      fontSize="xs"
    >
      <Box
        w="100%"
        borderRadius="md"
        px={1}
        py={0.5}
        cursor={isRunClickable ? "pointer" : undefined}
        role={isRunClickable ? "button" : undefined}
        tabIndex={isRunClickable ? 0 : undefined}
        aria-label={
          isRunClickable ? `Open artifact: ${labelParts.prefix}${labelParts.artifactHighlight ?? ""}` : undefined
        }
        onClick={
          isRunClickable
            ? e => {
                e.stopPropagation();
                openWithOpts();
              }
            : undefined
        }
        onKeyDown={
          isRunClickable
            ? e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  openWithOpts();
                }
              }
            : undefined
        }
        _hover={isRunClickable ? { bg: "gray.100" } : undefined}
        _focusVisible={
          isRunClickable ? { outline: "2px solid", outlineColor: "purple.300", outlineOffset: "2px" } : undefined
        }
      >
        <HStack align="center" spacing={3} w="100%">
          <Flex direction="column" align="center" justify="space-between" w="20px" h="25px" flexShrink={0}>
            <Box
              w="1px"
              h="3px"
              flexShrink={0}
              borderRadius="full"
              bg={topConnectorBg}
              opacity={isFirstRun ? 0 : 0.9}
            />
            <Flex
              align="center"
              justify="center"
              w="20px"
              h="19px"
              borderRadius="full"
              position="relative"
              flexShrink={0}
            >
              {node}
            </Flex>
            <Box
              w="1px"
              h="3px"
              flexShrink={0}
              borderRadius="full"
              bg={isLastRun ? "transparent" : done ? "gray.300" : "gray.200"}
              opacity={isLastRun ? 0 : 0.9}
            />
          </Flex>
          <Box flex={1} minW={0}>
            {renderLabel()}
          </Box>
        </HStack>
      </Box>
    </Tooltip>
  );
};

export default React.memo(ToolRunRow);
